/**
 * Royal Menu JavaScript
 * Handles interactions and accessibility for the Royal Menu system
 */

class RoyalMenu {
  constructor() {
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupMoreMenuPanels();
    this.setupKeyboardNavigation();
    this.setupAccessibility();
  }

  setupMoreMenuPanels() {
    const moreMenu = document.querySelector('.more-menu');
    if (!moreMenu) return;

    const container = moreMenu.querySelector('.more-panel-container');
    if (!container) return;

    const t1Items = container.querySelectorAll('.more-t1-item');
    const t2Panels = container.querySelectorAll('.more-col-tier2');
    const t3Panels = container.querySelectorAll('.more-col-tier3');

    // Activate a specific T2 panel and its first T2 item + corresponding T3 panel
    const activateT1 = (t1Index) => {
      // Update T1 active state
      t1Items.forEach(i => i.classList.toggle('active', i.getAttribute('data-t1-index') === String(t1Index)));

      // Show matching T2 panel, hide others
      t2Panels.forEach(p => {
        const isMatch = p.getAttribute('data-t2-panel') === String(t1Index);
        p.classList.toggle('active', isMatch);
      });

      // Auto-activate first T2 item in the newly visible T2 panel
      const activeT2Panel = container.querySelector(`.more-col-tier2[data-t2-panel="${t1Index}"]`);
      if (activeT2Panel) {
        const t2Items = activeT2Panel.querySelectorAll('.more-t2-item');
        t2Items.forEach((item, idx) => {
          item.classList.toggle('active', idx === 0);
        });
        // Show T3 for first T2 item
        if (t2Items.length > 0) {
          const firstT2Idx = t2Items[0].getAttribute('data-t2-index');
          activateT3(t1Index, firstT2Idx);
        } else {
          // No T2 items — hide all T3 panels
          activateT3(t1Index, null);
        }
        // Wire T2 hover on this panel
        this.wireT2Panel(container, activeT2Panel, t1Index);
      }
    };

    // Show matching T3 panel for a given T1+T2 pairing
    const activateT3 = (t1Index, t2Index) => {
      t3Panels.forEach(p => {
        const key = p.getAttribute('data-t3-panel');
        const isMatch = (t2Index !== null) && (key === `${t1Index}-${t2Index}`);
        p.classList.toggle('active', isMatch);
      });
    };

    // Store activateT3 on instance so wireT2Panel can access it
    this._activateT3 = activateT3;

    // Wire T1 hover
    t1Items.forEach(item => {
      item.addEventListener('mouseenter', () => {
        const idx = item.getAttribute('data-t1-index');
        activateT1(idx);
      });
    });

    // Initialise: auto-select first T1 item when dropdown opens
    // We hook into showDropdown's callback via the More menu's mouseenter
    moreMenu.addEventListener('mouseenter', () => {
      const firstT1 = t1Items[0];
      if (firstT1) {
        activateT1(firstT1.getAttribute('data-t1-index'));
      }
      // Lock the container height to T1's natural height after layout is painted
      requestAnimationFrame(() => this.lockContainerToT1Height(container));
    });
  }

  lockContainerToT1Height(container) {
    const t1Col = container.querySelector('.more-col-tier1');
    if (!t1Col) return;
    // Temporarily remove any inline height so T1 re-flows naturally
    container.style.height = '';
    // Read T1's natural scroll height (all its items)
    const t1Height = t1Col.scrollHeight;
    // Pin the container to exactly that height
    container.style.height = t1Height + 'px';
  }

  wireT2Panel(container, t2Panel, t1Index) {
    const t2Items = t2Panel.querySelectorAll('.more-t2-item');
    t2Items.forEach(item => {
      if (item.dataset.t2Wired) return;
      item.dataset.t2Wired = '1';
      item.addEventListener('mouseenter', () => {
        t2Items.forEach(i => i.classList.toggle('active', i === item));
        const t2Idx = item.getAttribute('data-t2-index');
        this._activateT3(t1Index, t2Idx);
      });
    });
  }



  setupEventListeners() {
    // Handle mouse enter/leave for header menu items
    const menuItems = document.querySelectorAll('.header-menu-item');

    menuItems.forEach(item => {
      const dropdown = item.querySelector('.megamenu-dropdown');

      if (dropdown) {
        // Mouse events
        item.addEventListener('mouseenter', () => this.showDropdown(item));
        item.addEventListener('mouseleave', () => this.hideDropdown(item));
      }
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.header-menu-item')) {
        this.closeAllDropdowns();
      }
    });

    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });
  }

  setupKeyboardNavigation() {
    const menuItems = document.querySelectorAll('.header-menu-item');

    menuItems.forEach((item, index) => {
      const link = item.querySelector('.header-menu-link');
      const dropdown = item.querySelector('.megamenu-dropdown');

      if (link) {
        link.addEventListener('keydown', (e) => {
          switch (e.key) {
            case 'Enter':
            case ' ':
              if (dropdown) {
                e.preventDefault();
                this.showDropdown(item);
                this.focusFirstDropdownItem(dropdown);
              }
              break;
            case 'ArrowDown':
              e.preventDefault();
              if (dropdown) {
                this.showDropdown(item);
                this.focusFirstDropdownItem(dropdown);
              } else {
                this.focusNextNavItem(index);
              }
              break;
            case 'ArrowUp':
              e.preventDefault();
              this.focusPreviousNavItem(index);
              break;
            case 'ArrowRight':
              e.preventDefault();
              this.focusNextNavItem(index);
              break;
            case 'ArrowLeft':
              e.preventDefault();
              this.focusPreviousNavItem(index);
              break;
          }
        });
      }

      // Handle keyboard navigation within dropdown
      if (dropdown) {
        const dropdownLinks = dropdown.querySelectorAll('.megamenu-column-title a, .megamenu-sublinks a');

        dropdownLinks.forEach((dropdownLink, linkIndex) => {
          dropdownLink.addEventListener('keydown', (e) => {
            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                this.focusNextDropdownItem(dropdownLinks, linkIndex);
                break;
              case 'ArrowUp':
                e.preventDefault();
                this.focusPreviousDropdownItem(dropdownLinks, linkIndex);
                break;
              case 'Escape':
                e.preventDefault();
                this.hideDropdown(item);
                link.focus();
                break;
            }
          });
        });
      }
    });
  }

  setupAccessibility() {
    const menuItems = document.querySelectorAll('.header-menu-item');

    menuItems.forEach(item => {
      const dropdown = item.querySelector('.megamenu-dropdown');
      const link = item.querySelector('.header-menu-link');

      if (dropdown && link) {
        // Set ARIA attributes
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');

        // Update ARIA expanded state
        const observer = new MutationObserver(() => {
          const isVisible = dropdown.style.visibility === 'visible';
          link.setAttribute('aria-expanded', isVisible);
        });

        observer.observe(dropdown, {
          attributes: true,
          attributeFilter: ['style']
        });
      }
    });
  }

  showDropdown(item) {
    const dropdown = item.querySelector('.megamenu-dropdown');
    if (dropdown) {
      // Close other dropdowns first
      this.closeAllDropdowns();

      // Calculate collision logic before displaying if it is a More menu
      if (item.classList.contains('more-menu')) {
        this.checkDropdownCollisions(dropdown);
      }

      // Show this dropdown
      dropdown.style.opacity = '1';
      dropdown.style.visibility = 'visible';
      dropdown.style.transform = 'translateY(0)';

      // Update ARIA
      const link = item.querySelector('.header-menu-link');
      if (link) {
        link.setAttribute('aria-expanded', 'true');
      }
    }
  }

  hideDropdown(item) {
    const dropdown = item.querySelector('.megamenu-dropdown');
    if (dropdown) {
      dropdown.style.opacity = '0';
      dropdown.style.visibility = 'hidden';
      dropdown.style.transform = 'translateY(-10px)';

      // Update ARIA
      const link = item.querySelector('.header-menu-link');
      if (link) {
        link.setAttribute('aria-expanded', 'false');
      }
    }
  }

  toggleDropdown(item) {
    const dropdown = item.querySelector('.megamenu-dropdown');
    if (dropdown) {
      const isVisible = dropdown.style.visibility === 'visible';
      if (isVisible) {
        this.hideDropdown(item);
      } else {
        this.showDropdown(item);
      }
    }
  }

  checkDropdownCollisions(dropdown) {
    const nestedDropdowns = dropdown.querySelectorAll('.nested-dropdown, .third-level-dropdown');

    // Reset all flipping classes first
    nestedDropdowns.forEach(nested => {
      nested.classList.remove('open-left');
    });

    // We bind a hover listener to individual dropdown items, which triggers collision check efficiently ONLY when needed
    const itemsWithDropdowns = dropdown.querySelectorAll('.dropdown-item, .nested-item');
    itemsWithDropdowns.forEach(item => {
      item.addEventListener('mouseenter', () => {
        const nestedMenu = item.querySelector(':scope > .nested-dropdown, :scope > .third-level-dropdown');
        if (!nestedMenu) return;

        // Temporarily make it visible to measure it without visually showing it yet using opacity
        const originalVisibility = nestedMenu.style.visibility;
        const originalOpacity = nestedMenu.style.opacity;

        nestedMenu.style.visibility = 'visible';
        nestedMenu.style.opacity = '0';

        const rect = nestedMenu.getBoundingClientRect();

        // If the right edge of the submenu would go past the window width (with 20px buffer)
        if (rect.right > window.innerWidth - 20) {
          nestedMenu.classList.add('open-left');
        } else {
          nestedMenu.classList.remove('open-left');
        }

        // Reset properties
        nestedMenu.style.visibility = originalVisibility;
        nestedMenu.style.opacity = originalOpacity;
      }, { once: true }); // Only calculate collision on the first hover per menu opening
    });
  }

  closeAllDropdowns() {
    // Close mega menu dropdowns
    const dropdowns = document.querySelectorAll('.megamenu-dropdown');
    dropdowns.forEach(dropdown => {
      dropdown.style.opacity = '0';
      dropdown.style.visibility = 'hidden';
      dropdown.style.transform = 'translateY(-10px)';
    });

    // Update all ARIA expanded states
    const links = document.querySelectorAll('.header-menu-link[aria-haspopup="true"]');
    links.forEach(link => {
      link.setAttribute('aria-expanded', 'false');
    });
  }

  focusFirstDropdownItem(dropdown) {
    const firstLink = dropdown.querySelector('.megamenu-column-title a, .megamenu-sublinks a, .dropdown-item > a');
    if (firstLink) {
      firstLink.focus();
    }
  }

  focusNextNavItem(currentIndex) {
    const navItems = document.querySelectorAll('.header-menu-item, .more-menu');
    const nextIndex = (currentIndex + 1) % navItems.length;
    const nextLink = navItems[nextIndex].querySelector('.header-menu-link');
    if (nextLink) nextLink.focus();
  }

  focusPreviousNavItem(currentIndex) {
    const navItems = document.querySelectorAll('.header-menu-item, .more-menu');
    const prevIndex = currentIndex === 0 ? navItems.length - 1 : currentIndex - 1;
    const prevLink = navItems[prevIndex].querySelector('.header-menu-link');
    if (prevLink) prevLink.focus();
  }

  focusNextDropdownItem(links, currentIndex) {
    const nextIndex = (currentIndex + 1) % links.length;
    links[nextIndex].focus();
  }

  focusPreviousDropdownItem(links, currentIndex) {
    const prevIndex = currentIndex === 0 ? links.length - 1 : currentIndex - 1;
    links[prevIndex].focus();
  }
}



// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new RoyalMenu();
});

// Handle window resize
let resizeTimer;